import React, { useState, useEffect } from 'react';
import { COLORS } from '../config/constants';
import api from '../config/api';
import UserPermissions from '../components/UserPermissions';
import SMTPSettings from '../components/SMTPSettings';

const Settings = ({ user }) => {
  const [activeTab, setActiveTab] = useState('locations');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    name: '',
    type: 'exit', // exit (çıkış yeri) veya destination (sevk yeri)
    address: '',
    notes: ''
  });

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Lokasyonlar yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      if (editingLocation) {
        await api.put(`/locations/${editingLocation.id}`, formData);
        setMessage({ type: 'success', text: 'Lokasyon güncellendi!' });
      } else {
        await api.post('/locations', formData);
        setMessage({ type: 'success', text: 'Lokasyon eklendi!' });
      }
      
      setShowForm(false);
      setEditingLocation(null);
      resetForm();
      loadLocations();
    } catch (error) {
      setMessage({ type: 'error', text: 'İşlem başarısız!' });
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      type: location.type,
      address: location.address || '',
      notes: location.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu lokasyonu silmek istediğinizden emin misiniz?')) return;
    
    try {
      await api.delete(`/locations/${id}`);
      setMessage({ type: 'success', text: 'Lokasyon silindi!' });
      loadLocations();
    } catch (error) {
      setMessage({ type: 'error', text: 'Silme işlemi başarısız!' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'exit',
      address: '',
      notes: ''
    });
  };

  const canEdit = user?.role === 'admin' || user?.role === 'operator';

  const exitLocations = locations.filter(l => l.type === 'exit');
  const destinationLocations = locations.filter(l => l.type === 'destination');

  return (
    <div style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: COLORS.text, margin: 0 }}>
          Ayarlar
        </h1>
        <p style={{ color: COLORS.textLight, marginTop: '0.5rem' }}>
          Sistem ayarları ve yetkilendirme
        </p>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '2rem',
        borderBottom: `2px solid ${COLORS.background}`
      }}>
        <button
          onClick={() => setActiveTab('locations')}
          style={{
            padding: '1rem 2rem',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'locations' ? `3px solid ${COLORS.primary}` : 'none',
            color: activeTab === 'locations' ? COLORS.primary : COLORS.textLight,
            fontWeight: activeTab === 'locations' ? '600' : '500',
            fontSize: '1rem',
            cursor: 'pointer',
            marginBottom: '-2px',
            transition: 'all 0.2s'
          }}
        >
          📍 Lokasyonlar
        </button>
        {user?.role === 'admin' && (
          <>
            <button
              onClick={() => setActiveTab('permissions')}
              style={{
                padding: '1rem 2rem',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'permissions' ? `3px solid ${COLORS.primary}` : 'none',
                color: activeTab === 'permissions' ? COLORS.primary : COLORS.textLight,
                fontWeight: activeTab === 'permissions' ? '600' : '500',
                fontSize: '1rem',
                cursor: 'pointer',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              👥 Yetkilendirme
            </button>
            <button
              onClick={() => setActiveTab('smtp')}
              style={{
                padding: '1rem 2rem',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'smtp' ? `3px solid ${COLORS.primary}` : 'none',
                color: activeTab === 'smtp' ? COLORS.primary : COLORS.textLight,
                fontWeight: activeTab === 'smtp' ? '600' : '500',
                fontSize: '1rem',
                cursor: 'pointer',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              📧 Mail Ayarları
            </button>
          </>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'permissions' ? (
        <UserPermissions currentUser={user} />
      ) : activeTab === 'smtp' ? (
        <SMTPSettings />
      ) : (
        <>
          {/* Locations Tab Content */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: COLORS.text, margin: 0 }}>
                Sevk Yerleri ve Çıkış Noktaları
              </h2>
            </div>
            {canEdit && (
              <button
                onClick={() => {
                  setShowForm(!showForm);
                  setEditingLocation(null);
                  resetForm();
                }}
                style={{
                  padding: '0.875rem 1.5rem',
                  background: COLORS.primary,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>{showForm ? '✕' : '+'}</span>
                {showForm ? 'İptal' : 'Yeni Lokasyon'}
              </button>
            )}
          </div>

      {/* Message */}
      {message.text && (
        <div
          style={{
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '2rem',
            background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: message.type === 'success' ? '#065f46' : '#991b1b',
            border: `1px solid ${message.type === 'success' ? '#6ee7b7' : '#fca5a5'}`
          }}
        >
          {message.text}
        </div>
      )}

      {/* Form */}
      {showForm && canEdit && (
        <div style={{ 
          background: COLORS.white, 
          borderRadius: '12px', 
          padding: '2rem', 
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)' 
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: COLORS.text, marginBottom: '1.5rem' }}>
            {editingLocation ? 'Lokasyonu Düzenle' : 'Yeni Lokasyon Ekle'}
          </h2>
          
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
              <FormField 
                label="Lokasyon Adı" 
                value={formData.name} 
                onChange={(v) => setFormData({...formData, name: v})} 
                required 
              />
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: COLORS.text }}>
                  Tip <span style={{ color: COLORS.primary }}>*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="exit">Çıkış Yeri</option>
                  <option value="destination">Sevk Yeri</option>
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <FormField 
                  label="Adres" 
                  value={formData.address} 
                  onChange={(v) => setFormData({...formData, address: v})} 
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: COLORS.text }}>
                  Notlar
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button
                type="submit"
                style={{
                  padding: '1rem 2rem',
                  background: COLORS.primary,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {editingLocation ? 'Güncelle' : 'Ekle'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingLocation(null);
                  resetForm();
                }}
                style={{
                  padding: '1rem 2rem',
                  background: COLORS.background,
                  color: COLORS.text,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lists */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
        {/* Çıkış Yerleri */}
        <div style={{ 
          background: COLORS.white, 
          borderRadius: '12px', 
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)' 
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.text, marginBottom: '1rem' }}>
            📤 Çıkış Yerleri
          </h3>
          {loading ? (
            <p style={{ color: COLORS.textLight, textAlign: 'center', padding: '2rem' }}>Yükleniyor...</p>
          ) : exitLocations.length === 0 ? (
            <p style={{ color: COLORS.textLight, textAlign: 'center', padding: '2rem' }}>Henüz çıkış yeri eklenmemiş</p>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {exitLocations.map((location) => (
                <div
                  key={location.id}
                  style={{
                    padding: '1rem',
                    border: `1px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = COLORS.background}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, marginBottom: '0.5rem', fontWeight: '600', color: COLORS.text }}>
                        {location.name}
                      </h4>
                      {location.address && (
                        <p style={{ margin: 0, fontSize: '0.85rem', color: COLORS.textLight }}>
                          {location.address}
                        </p>
                      )}
                      {location.notes && (
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: COLORS.textLight, fontStyle: 'italic' }}>
                          {location.notes}
                        </p>
                      )}
                    </div>
                    {canEdit && (
                      <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                        <button
                          onClick={() => handleEdit(location)}
                          style={{
                            padding: '0.5rem',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.2rem'
                          }}
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        {user?.role === 'admin' && (
                          <button
                            onClick={() => handleDelete(location.id)}
                            style={{
                              padding: '0.5rem',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '1.2rem'
                            }}
                            title="Sil"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sevk Yerleri */}
        <div style={{ 
          background: COLORS.white, 
          borderRadius: '12px', 
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)' 
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.text, marginBottom: '1rem' }}>
            📍 Sevk Yerleri
          </h3>
          {loading ? (
            <p style={{ color: COLORS.textLight, textAlign: 'center', padding: '2rem' }}>Yükleniyor...</p>
          ) : destinationLocations.length === 0 ? (
            <p style={{ color: COLORS.textLight, textAlign: 'center', padding: '2rem' }}>Henüz sevk yeri eklenmemiş</p>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {destinationLocations.map((location) => (
                <div
                  key={location.id}
                  style={{
                    padding: '1rem',
                    border: `1px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = COLORS.background}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, marginBottom: '0.5rem', fontWeight: '600', color: COLORS.text }}>
                        {location.name}
                      </h4>
                      {location.address && (
                        <p style={{ margin: 0, fontSize: '0.85rem', color: COLORS.textLight }}>
                          {location.address}
                        </p>
                      )}
                      {location.notes && (
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: COLORS.textLight, fontStyle: 'italic' }}>
                          {location.notes}
                        </p>
                      )}
                    </div>
                    {canEdit && (
                      <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                        <button
                          onClick={() => handleEdit(location)}
                          style={{
                            padding: '0.5rem',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.2rem'
                          }}
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        {user?.role === 'admin' && (
                          <button
                            onClick={() => handleDelete(location.id)}
                            style={{
                              padding: '0.5rem',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '1.2rem'
                            }}
                            title="Sil"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

const FormField = ({ label, value, onChange, type = 'text', required = false }) => (
  <div>
    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: COLORS.text }}>
      {label} {required && <span style={{ color: COLORS.primary }}>*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      style={{
        width: '100%',
        padding: '0.75rem',
        border: `1px solid ${COLORS.background}`,
        borderRadius: '8px',
        fontSize: '1rem',
        outline: 'none'
      }}
    />
  </div>
);

export default Settings;
